import React, { useEffect, useState } from "react";
import { Layout } from "antd";
import { useParams } from "react-router-dom";
import APIService from "../../Service/APIService";
import GenericDashboard from "../GenericDashboard/GenericDashboard";
import moment from "moment";

import Card from "../../common/card";
import LastOrderCard from "../../common/last-order-card";
import PastDueAlert from "../../common/past-due-alert";
import Text from "../../common/typography/text";
import Button from "../../common/button/button";
import SectionHeader from "../../common/section-header/index.js";

const { Content } = Layout;

const AccountHomeTab = ({ accountDetails, filterButton }) => {
  const { id } = useParams();
  const isYTD = filterButton === "YTD";

  const [loading, setLoading] = useState(true);

  const [balancePastDue, setBalancePastDue] = useState(0);
  const [invoicesPastDue, setInvoicesPastDue] = useState(0);
  const [purchasedYTD, setPurchasedYTD] = useState(0);
  const [purchasedMTD, setPurchasedMTD] = useState(0);
  const [openBalance, setOpenBalance] = useState(0);
  const [openBalanceCount, setOpenBalanceCount] = useState(0);
  const [totalInvoiceCount, setTotalInvoiceCount] = useState(0);
  const [invoicesThisYearCount, setInvoicesThisYearCount] = useState(0);
  const [invoicesThisMonthCount, setInvoicesThisMonthCount] = useState(0);
  const [openBalanceInvoices, setOpenBalanceInvoices] = useState([]);
  const [daysSinceLastDirectOrder, setDaysSinceLastDirectOrder] =
    useState(null);
  const [lastDirectOrderDate, setLastDirectOrderDate] = useState(null);
  const [canShowTotalPurchasedAllAccounts, setCanShowTotalPurchasedAllAccounts] =
    useState(false);
  const [totalPurchasedAllAccountsYTD, setTotalPurchasedAllAccountsYTD] =
    useState(0);
  const [totalPurchasedAllAccountsMTD, setTotalPurchasedAllAccountsMTD] =
    useState(0);
  const [
    totalPurchasedAllAccountsInvoiceCountYTD,
    setTotalPurchasedAllAccountsInvoiceCountYTD,
  ] = useState(0);
  const [
    totalPurchasedAllAccountsInvoiceCountMTD,
    setTotalPurchasedAllAccountsInvoiceCountMTD,
  ] = useState(0);
  const [openBalanceAllAccounts, setOpenBalanceAllAccounts] = useState(0);
  const [openBalanceAllAccountsCount, setOpenBalanceAllAccountsCount] =
    useState(0);
  const [balancePastDueAllAccounts, setBalancePastDueAllAccounts] = useState(0);
  const [invoicesPastDueAllAccounts, setInvoicesPastDueAllAccounts] =
    useState(0);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentOrdersAllAccounts, setRecentOrdersAllAccounts] = useState([]);
  const [recentOrdersScope, setRecentOrdersScope] = useState("current");

  const getAccountHomeData = async () => {
    const api = new APIService();
    const response = await api.getDataFromBackend(
      `accounthomeinvoicedata?AccountID=${id}`
    );
    return response?.data;
  };

  // Recent Orders data is not part of accounthomeinvoicedata, so we pull the
  // order lists directly and scope them to this account (and its child
  // accounts) on the client.
  const getRecentOrdersData = async () => {
    const api = new APIService();

    const [directRes, consignmentRes, productUsageRes, childAccountsRes] =
      await Promise.all([
        api.getDataFromBackend("getdirectorders?type=All"),
        api.getDataFromBackend("getconsignmentorders?type=All"),
        api.getDataFromBackend("getproductusageorders?type=All"),
        api.getDataFromBackend(`getallchildaccounts/?ParentAccountID=${id}`),
      ]);

    // Tag each order with its type from the source endpoint so the "View"
    // link can route to the correct order-detail page regardless of how the
    // backend labels OrderType.
    const withType = (rows, orderType) =>
      (rows ?? []).map((order) => ({ ...order, OrderType: orderType }));

    const orders = [
      ...withType(directRes?.data?.data, "Direct Order"),
      ...withType(consignmentRes?.data?.data, "Consignment Order"),
      ...withType(productUsageRes?.data?.data, "Product Usage"),
    ];
    const childAccountIds = (childAccountsRes?.data?.data ?? []).map((child) =>
      Number(child.ID)
    );

    return { orders, childAccountIds };
  };

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      setLoading(true);

      try {
        const data = await getAccountHomeData();

        setBalancePastDue(data?.balancePastDue ?? 0);
        setInvoicesPastDue(data?.invoicesPastDue ?? 0);
        setPurchasedYTD(data?.totalPurchasedYTD ?? 0);
        setPurchasedMTD(data?.totalPurchasedMTD ?? 0);
        setOpenBalance(data?.openBalance ?? 0);
        setOpenBalanceCount(data?.openBalanceCount ?? 0);
        setTotalInvoiceCount(data?.totalInvoiceCount ?? 0);
        setInvoicesThisYearCount(data?.invoicesThisYearCount ?? 0);
        setInvoicesThisMonthCount(data?.invoicesThisMonthCount ?? 0);
        setDaysSinceLastDirectOrder(data?.DaysSinceLastDirectOrder ?? null);
        setLastDirectOrderDate(data?.LastDirectOrderDate ?? null);
        setCanShowTotalPurchasedAllAccounts(
          data?.CanShowTotalPurchasedAllAccounts === true
        );
        setTotalPurchasedAllAccountsYTD(
          data?.TotalPurchasedAllAccountsYTD ?? 0
        );
        setTotalPurchasedAllAccountsMTD(
          data?.TotalPurchasedAllAccountsMTD ?? 0
        );
        setTotalPurchasedAllAccountsInvoiceCountYTD(
          data?.TotalPurchasedAllAccountsInvoiceCountYTD ?? 0
        );
        setTotalPurchasedAllAccountsInvoiceCountMTD(
          data?.TotalPurchasedAllAccountsInvoiceCountMTD ?? 0
        );
        setOpenBalanceAllAccounts(data?.OpenBalanceAllAccounts ?? 0);
        setOpenBalanceAllAccountsCount(
          data?.OpenBalanceAllAccountsCount ?? 0
        );
        setBalancePastDueAllAccounts(data?.BalancePastDueAllAccounts ?? 0);
        setInvoicesPastDueAllAccounts(
          data?.InvoicesPastDueAllAccounts ?? 0
        );

        const formattedInvoices =
          data?.openBalanceInvoices?.map((invoice) => {
            const dueDays = moment(invoice.InvoiceDueDate).diff(moment(), "days");

            return {
              ...invoice,
              DueIn: dueDays < 0 ? "Past Due" : `${dueDays + 1} days`,
              InvoiceDate: moment(invoice.InvoiceDate).format("MM-DD-YYYY"),
              InvoiceDueDate: moment(invoice.InvoiceDueDate).format("MM-DD-YYYY"),
              OrderDate: moment(invoice.OrderDate).format("MM-DD-YYYY"),
              InvoiceTotal: `$${Number(invoice.InvoiceTotal).toLocaleString(
                "en-US",
                { maximumFractionDigits: 2 }
              )}`,
              OpenBalance: `$${(
                Number(invoice.InvoiceTotal) +
                Number(invoice.ShippingCost ?? 0) -
                Number(invoice.TotalPaymentsApplied)
              ).toLocaleString("en-US", { maximumFractionDigits: 2 })}`,
            };
          }) ?? [];

        setOpenBalanceInvoices(formattedInvoices);

        const accountId = Number(id);
        const { orders, childAccountIds } = await getRecentOrdersData();
        const familyAccountIds = new Set([accountId, ...childAccountIds]);

        const formatOrders = (rawOrders) =>
          rawOrders
            .slice()
            .sort((a, b) => moment(b.OrderDate).diff(moment(a.OrderDate)))
            .map((order) => ({
              ...order,
              OrderDate: moment(order.OrderDate).format("MM-DD-YYYY"),
              OrderTotal: `$${Number(order.OrderTotal).toLocaleString("en-US", {
                maximumFractionDigits: 2,
              })}`,
            }));

        // "Current Only" = orders shipped to this account.
        // "All Accounts" = orders shipped to this account or any of its
        // children (the same family used for the aggregated metrics above).
        setRecentOrders(
          formatOrders(
            orders.filter((order) => Number(order.ShipToAccountID) === accountId)
          )
        );
        setRecentOrdersAllAccounts(
          formatOrders(
            orders.filter((order) =>
              familyAccountIds.has(Number(order.ShipToAccountID))
            )
          )
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const pastDueWarningThreshold =
    accountDetails?.PastDueWarning != null
      ? Number(accountDetails.PastDueWarning)
      : null;
  const pastDueLimitThreshold =
    accountDetails?.PastDueLimit != null
      ? Number(accountDetails.PastDueLimit)
      : null;
  const isChildAccount =
    accountDetails?.IsChildAccount === true ||
    accountDetails?.IsChildAccount === 1 ||
    accountDetails?.IsChildAccount === "1";
  const isPastDueWarningExceeded =
    pastDueWarningThreshold !== null && balancePastDue > pastDueWarningThreshold;
  const isPastDueLimitExceeded =
    pastDueLimitThreshold !== null && balancePastDue > pastDueLimitThreshold;
  const shouldShowPastDueWarning =
    isPastDueWarningExceeded && !isPastDueLimitExceeded;
  const shouldShowTotalPurchasedAllAccounts =
    canShowTotalPurchasedAllAccounts && !isChildAccount;
  const canScopeRecentOrders = shouldShowTotalPurchasedAllAccounts;
  const displayedRecentOrders =
    canScopeRecentOrders && recentOrdersScope === "all"
      ? recentOrdersAllAccounts
      : recentOrders;

  return (
    <div style={{ display: "flex" }}>
      <Content>
        {(shouldShowPastDueWarning || isPastDueLimitExceeded) && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              marginBottom: "32px",
            }}
          >
            {shouldShowPastDueWarning && (
              <PastDueAlert
                variant="warning"
                thresholdValue={pastDueWarningThreshold}
              />
            )}
            {isPastDueLimitExceeded && (
              <PastDueAlert
                variant="limit"
                thresholdValue={pastDueLimitThreshold}
              />
            )}
          </div>
        )}

        {/* ALL ACCOUNTS SECTION (parent accounts only) */}
        {shouldShowTotalPurchasedAllAccounts && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 16,
              padding: 16,
              borderRadius: 16,
              background: "var(--greyBackground, #F4F4F4)",
              marginBottom: 32,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <Text variant="bodyText">All Accounts</Text>
              <Text variant="smallText" color="lightText">
                Aggregated totals for all accounts under this account
              </Text>
            </div>

            <div className="home-card-wrapper">
              <Card
                loading={loading}
                cardTitle="Total Purchased for all Accounts"
                cardTitleValue={
                  isYTD
                    ? totalPurchasedAllAccountsYTD
                    : totalPurchasedAllAccountsMTD
                }
                cardBottomNumber={
                  isYTD
                    ? totalPurchasedAllAccountsInvoiceCountYTD
                    : totalPurchasedAllAccountsInvoiceCountMTD
                }
                cardBottomName="Invoices"
              />

              <Card
                loading={loading}
                cardTitle="Open Balance for all Accounts"
                cardTitleValue={openBalanceAllAccounts}
                cardBottomNumber={openBalanceAllAccountsCount}
                cardBottomName="Invoices"
              />

              <Card
                loading={loading}
                cardTitle="Balance Past Due for all Accounts"
                cardTitleValue={balancePastDueAllAccounts}
                cardTitleValueColor="failureColor"
                cardBottomNumber={invoicesPastDueAllAccounts}
                cardBottomName="Invoices"
              />
            </div>
          </div>
        )}

        {/* TOP CARDS */}
        <div className="home-card-wrapper">
          <Card
            loading={loading}
            cardTitle="Total Purchased"
            cardTitleValue={isYTD ? purchasedYTD : purchasedMTD}
            // cardBottomNumber={totalInvoiceCount}
            cardBottomNumber={isYTD ? invoicesThisYearCount : invoicesThisMonthCount}
            cardBottomName="Invoices"
          />

          <Card
            loading={loading}
            cardTitle="Open Balance"
            cardTitleValue={openBalance}
            cardBottomNumber={openBalanceCount}
            cardBottomName="Invoices"
          />

          <Card
            loading={loading}
            cardTitle="Balance Past Due"
            cardTitleValue={balancePastDue}
            cardTitleValueColor="failureColor"
            cardBottomNumber={invoicesPastDue}
            cardBottomName="Invoices"
          />


          <LastOrderCard
            loading={loading}
            daysSinceLastOrder={daysSinceLastDirectOrder}
            lastOrderDate={lastDirectOrderDate}
          />
        </div>

        {/* TABLE SECTION */}
        {(loading || openBalanceInvoices.length > 0) && (
          <>
            <SectionHeader title="Invoices with Open Balance" showButton={false} />

            <GenericDashboard
              data={openBalanceInvoices}
              screen="AccountHomeInvoiceTable"
              loading={loading}
            />
          </>
        )}

        {/* RECENT ORDERS SECTION */}
        {(loading ||
          recentOrders.length > 0 ||
          recentOrdersAllAccounts.length > 0) && (
          <>
            <SectionHeader
              title="Recent Orders"
              showButton={false}
              rightContent={
                canScopeRecentOrders && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Text variant="buttonText">Scope</Text>
                    <Button
                      isActive={recentOrdersScope === "current"}
                      variant="timeframe"
                      onClick={() => setRecentOrdersScope("current")}
                    >
                      Current Only
                    </Button>
                    <Button
                      isActive={recentOrdersScope === "all"}
                      variant="timeframe"
                      onClick={() => setRecentOrdersScope("all")}
                    >
                      All Accounts
                    </Button>
                  </div>
                )
              }
            />

            <GenericDashboard
              data={displayedRecentOrders}
              screen="AccountHomeRecentOrders"
              loading={loading}
            />
          </>
        )}
      </Content>
    </div>
  );
};

export default AccountHomeTab;
